import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useMemo,
} from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Pagination,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";
import {
  getPaymentDueList,
  insertPaymentDue,
  updatePaymentDue,
  deletePaymentDue,
} from "../../../api/disticaret/vesselApi";

import {
  getCustomers,
  insertCustomer,
} from "../../../api/disticaret/customerApi";

const initialFormState = {
  companyId: "",
  company: "",
  country: "",
  order: "",
  amount: "",
  currency: "USD",
  dueDate: "",
  explaination: "",
  status: "pending",
  reminderEnabled: true,
  reminderStartDaysBeforeDue: 7,
  reminderRepeatEveryDays: 2,
  reminderMaxCount: 5,
  reminderTimeOfDay: "09:00",
  hasAdvancePayment: false,
  advancePaymentAmount: "",
  advancePaymentDate: "",
  payments: [],
};

function PaymentDueListe() {
  const { permissions } = useAuth();

  const canGetDetail = permissions?.includes("vessel_get_detail");
  const canPost = permissions?.includes("vessel_post");
  const canUpdate = permissions?.includes("vessel_update");
  const canDelete = permissions?.includes("vessel_delete");

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [visibleStatuses, setVisibleStatuses] = useState({
    pending: true,
    paid: true,
    cancelled: false,
    risky: true,
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState(initialFormState);

  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    companyName: "",
    region: "",
    sector: "",
  });

  const [filters, setFilters] = useState({
    q: "",
    company: "",
    currency: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const fmtNum = (num) =>
    num
      ? Number(num).toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0,00";

  const formatDateTR = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getFormDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  const getDueDateWarning = (dueDate, status) => {
    if ((status !== "pending" && status !== "risky") || !dueDate) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);

    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (status === "risky") {
      return {
        variant: "danger",
        text: "RISKLI KAYIT",
        rowClass: "table-danger",
      };
    } else if (diffDays < 0) {
      return { variant: "danger", text: "Gecikti", rowClass: "table-danger" };
    } else if (diffDays === 0) {
      return {
        variant: "danger",
        text: "Bugun Son Gun",
        rowClass: "table-danger",
      };
    } else if (diffDays <= 3) {
      return {
        variant: "warning",
        text: `${diffDays} Gun Kaldi`,
        rowClass: "table-warning",
      };
    } else if (diffDays <= 7) {
      return { variant: "info", text: "Yaklasiyor", rowClass: "" };
    }

    return null;
  };

  const fetchData = useCallback(
    async (overridePage = null) => {
      setIsLoading(true);
      try {
        const activeFilters = {};
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== "") activeFilters[key] = value;
        });

        const params = {
          page: overridePage || currentPage,
          limit: 25,
          ...activeFilters,
        };

        const res = await getPaymentDueList(params);
        const payload = res.data ? res.data : res;

        if (payload && payload.success) {
          setRows(payload.data.items || []);
          setTotalPages(payload.data.totalPages || 1);
          setTotalRecords(payload.data.total || 0);
        }
      } catch (error) {
        console.error("Hata:", error);
        if (error.response?.status === 403) {
          alert("Bu listeyi gormeye yetkiniz yok.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, filters]
  );

  const fetchCustomersList = async () => {
    try {
      const response = await getCustomers();
      if (response.data?.success || response.data?.statusCode === 200) {
        setCustomers(response.data.data);
      }
    } catch (error) {
      console.error("Musteri listesi cekilemedi", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCustomersList();
  }, [fetchData]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchData(1);
  };

  const handleClearFilters = () => {
    setFilters({ q: "", company: "", currency: "" });
    setCurrentPage(1);
  };

  const toggleStatusFilter = (statusKey) => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [statusKey]: !prev[statusKey],
    }));
  };

  const toggleRowDetails = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return " \u2195";
    return sortConfig.direction === "asc" ? " \u2191" : " \u2193";
  };

  const handleAddPayment = () => {
    setForm({
      ...form,
      payments: [...form.payments, { amount: "", paidAt: "", note: "" }],
    });
  };

  const handlePaymentChange = (index, field, value) => {
    const newPayments = [...form.payments];
    newPayments[index][field] = value;
    setForm({ ...form, payments: newPayments });
  };

  const handleRemovePayment = (index) => {
    const newPayments = form.payments.filter((_, i) => i !== index);
    setForm({ ...form, payments: newPayments });
  };

  const handleSave = async () => {
    if (!form.companyId || !form.order)
      return alert("Lutfen Sirket ve Order No seciniz/giriniz.");

    setIsLoading(true);
    try {
      const payload = { ...form };

      if (typeof payload.companyId === "object" && payload.companyId !== null) {
        payload.companyId = payload.companyId._id || payload.companyId.id;
      }

      if (canGetDetail) {
        payload.amount = Number(form.amount);
        if (payload.hasAdvancePayment) {
          payload.advancePaymentAmount = Number(payload.advancePaymentAmount);
        } else {
          payload.advancePaymentAmount = 0;
          payload.advancePaymentDate = null;
        }

        if (payload.payments && payload.payments.length > 0) {
          payload.payments = payload.payments.map((p) => ({
            ...p,
            amount: Number(p.amount),
          }));
        }
      } else {
        delete payload.amount;
        delete payload.currency;
        delete payload.hasAdvancePayment;
        delete payload.advancePaymentAmount;
        delete payload.advancePaymentDate;
        delete payload.payments;
      }

      if (payload.reminderEnabled) {
        payload.reminderStartDaysBeforeDue = Number(
          payload.reminderStartDaysBeforeDue
        );
        payload.reminderRepeatEveryDays = Number(
          payload.reminderRepeatEveryDays
        );
        payload.reminderMaxCount = Number(payload.reminderMaxCount);
      }

      if (editRow) {
        payload.id = editRow._id;
        await updatePaymentDue(payload);
        alert("Kayit basariyla guncellendi.");
      } else {
        await insertPaymentDue(payload);
        alert("Yeni kayit basariyla eklendi.");
      }

      await fetchData();
      setShowModal(false);
      setEditRow(null);
      setForm(initialFormState);
    } catch (error) {
      console.error("Kayit Hatasi:", error);
      alert("Islem sirasinda bir hata olustu.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteRow) return;

    setIsLoading(true);
    try {
      await deletePaymentDue({ id: deleteRow._id });
      alert("Kayit basariyla silindi.");
      setDeleteRow(null);
      fetchData();
    } catch (error) {
      console.error("Silme Hatasi:", error);
      alert("Silme islemi basarisiz.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomerChange = (e) => {
    setCustomerForm({ ...customerForm, [e.target.name]: e.target.value });
  };

  const handleSaveCustomer = async () => {
    if (!customerForm.companyName) return alert("Sirket adi zorunludur.");
    setSavingCustomer(true);
    try {
      const response = await insertCustomer(customerForm);
      if (response.data?.success || response.data?.statusCode === 200) {
        setShowCustomerModal(false);
        setCustomerForm({ companyName: "", region: "", sector: "" });
        await fetchCustomersList();
        alert("Musteri basariyla eklendi.");
      } else {
        alert("Musteri eklenemedi.");
      }
    } catch (error) {
      console.error("Musteri kayit hatasi", error);
      alert("Musteri eklenirken hata olustu.");
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleSelectCustomer = (e) => {
    const selectedId = e.target.value;
    const customer = customers.find((c) => (c._id || c.id) === selectedId);
    if (customer) {
      setForm({
        ...form,
        companyId: selectedId,
        company: customer.companyName,
        country: customer.region || "",
      });
    } else {
      setForm({ ...form, companyId: "", company: "", country: "" });
    }
  };

  const renderBadge = (status) => {
    let bg = "warning";
    let text = "dark";

    if (status === "paid") {
      bg = "success";
      text = "light";
    } else if (status === "cancelled") {
      bg = "dark";
      text = "light";
    } else if (status === "risky") {
      bg = "danger";
      text = "light";
    }

    return (
      <Badge
        bg={bg}
        text={text}
        style={{ fontSize: "11px", padding: "5px 8px" }}
      >
        {status ? status.toUpperCase() : ""}
      </Badge>
    );
  };

  const tableColSpan = 7 + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);

  const sortedAndFilteredRows = useMemo(() => {
    let filteredItems = rows.filter((r) => visibleStatuses[r.status] === true);

    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === "company") {
          aValue = (a.company || a.companyId?.companyName || "").toLowerCase();
          bValue = (b.company || b.companyId?.companyName || "").toLowerCase();
        } else if (sortConfig.key === "amount") {
          aValue =
            a.remainingAmount !== undefined
              ? Number(a.remainingAmount)
              : Number(a.amount || 0);
          bValue =
            b.remainingAmount !== undefined
              ? Number(b.remainingAmount)
              : Number(b.amount || 0);
        } else if (sortConfig.key === "dueDate") {
          aValue = new Date(a.dueDate || "1970-01-01").getTime();
          bValue = new Date(b.dueDate || "1970-01-01").getTime();
        }

        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filteredItems;
  }, [rows, visibleStatuses, sortConfig]);

  const analytics = sortedAndFilteredRows.reduce((acc, row) => {
    if (!row.amount || !row.currency) return acc;
    const cur = row.currency;

    if (!acc[cur]) {
      acc[cur] = { pending: 0, paid: 0, risky: 0, cancelled: 0, total: 0 };
    }

    const amt = Number(row.amount);
    acc[cur].total += amt;

    if (acc[cur][row.status] !== undefined) {
      acc[cur][row.status] += amt;
    }
    return acc;
  }, {});

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Odeme Takip</h4>
          <small className="text-muted">
            Toplam {totalRecords} kayit bulundu.
          </small>
        </div>

        {canPost && (
          <Button
            variant="success"
            size="sm"
            style={{ whiteSpace: "nowrap", height: "fit-content" }}
            onClick={() => {
              setForm(initialFormState);
              setEditRow(null);
              setShowModal(true);
            }}
          >
            Yeni Ekle
          </Button>
        )}
      </div>

      <div className="bg-white p-3 rounded shadow-sm border mb-4">
        <Row className="gy-3 align-items-end">
          <Col md={7} lg={8}>
            <Form onSubmit={handleSearch} className="d-flex gap-2 flex-wrap">
              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Genel Arama
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Ara (q)..."
                  value={filters.q}
                  onChange={(e) =>
                    setFilters({ ...filters, q: e.target.value })
                  }
                  style={{ width: "160px" }}
                />
              </div>
              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Sirket
                </Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Sirket Sec veya Yaz..."
                  value={filters.company}
                  onChange={(e) =>
                    setFilters({ ...filters, company: e.target.value })
                  }
                  list="company-search-list"
                  autoComplete="off"
                  style={{ width: "160px" }}
                />
                <datalist id="company-search-list">
                  {customers.map((c, index) => (
                    <option
                      key={c._id || c.id || index}
                      value={c.companyName}
                    />
                  ))}
                </datalist>
              </div>
              <div>
                <Form.Label className="small text-muted fw-bold mb-1">
                  Para Birimi
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.currency}
                  onChange={(e) =>
                    setFilters({ ...filters, currency: e.target.value })
                  }
                  style={{ width: "160px" }}
                >
                  <option value="">Tum Para Birimleri</option>
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                  <option value="SAR">SAR</option>
                </Form.Select>
              </div>
              <div className="d-flex align-items-end pb-1 gap-2">
                <Button size="sm" type="submit" variant="primary">
                  Ara
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleClearFilters}
                >
                  Temizle
                </Button>
              </div>
            </Form>
          </Col>

          <Col md={5} lg={4}>
            <div className="bg-light p-2 rounded border h-100">
              <span className="d-block text-muted small fw-bold border-bottom pb-1 mb-2">
                Gosterilecek Durumlar
              </span>
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  type="switch"
                  id="status-pending"
                  label="Pending"
                  checked={visibleStatuses.pending}
                  onChange={() => toggleStatusFilter("pending")}
                  className="text-warning fw-bold small"
                />
                <Form.Check
                  type="switch"
                  id="status-paid"
                  label="Paid"
                  checked={visibleStatuses.paid}
                  onChange={() => toggleStatusFilter("paid")}
                  className="text-success fw-bold small"
                />
                <Form.Check
                  type="switch"
                  id="status-risky"
                  label="Risky"
                  checked={visibleStatuses.risky}
                  onChange={() => toggleStatusFilter("risky")}
                  className="text-danger fw-bold small"
                />
                <Form.Check
                  type="switch"
                  id="status-cancelled"
                  label="Cancelled"
                  checked={visibleStatuses.cancelled}
                  onChange={() => toggleStatusFilter("cancelled")}
                  className="text-secondary fw-bold small"
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Card className="shadow-sm border-0 mb-4">
        <div className="table-responsive">
          {isLoading && !showModal && !deleteRow ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Yukleniyor...</p>
            </div>
          ) : (
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{ fontSize: "13px", whiteSpace: "nowrap" }}
            >
              <thead className="table-light">
                <tr style={{ height: "40px" }}>
                  <th
                    onClick={() => requestSort("company")}
                    style={{ cursor: "pointer" }}
                    title="Sirkete gore sirala"
                  >
                    SIRKET / ULKE {getSortIcon("company")}
                  </th>
                  <th
                    onClick={() => requestSort("order")}
                    style={{ cursor: "pointer" }}
                    title="Siparise gore sirala"
                  >
                    ORDER NO {getSortIcon("order")}
                  </th>
                  <th
                    onClick={() => requestSort("dueDate")}
                    style={{ cursor: "pointer" }}
                    title="Tarihe gore sirala"
                  >
                    VADE TARIHI {getSortIcon("dueDate")}
                  </th>
                  <th
                    className="text-end"
                    onClick={() => requestSort("amount")}
                    style={{ cursor: "pointer" }}
                    title="Tutara gore sirala"
                  >
                    KALAN TUTAR {getSortIcon("amount")}
                  </th>
                  <th
                    className="text-center"
                    onClick={() => requestSort("status")}
                    style={{ cursor: "pointer" }}
                    title="Duruma gore sirala"
                  >
                    DURUM {getSortIcon("status")}
                  </th>
                  <th>ACIKLAMA</th>
                  <th className="text-center">DETAY</th>
                  {canUpdate && <th className="text-center">DUZENLE</th>}
                  {canDelete && <th className="text-center">SIL</th>}
                </tr>
              </thead>
              <tbody>
                {sortedAndFilteredRows.length > 0 ? (
                  sortedAndFilteredRows.map((r) => {
                    const warning = getDueDateWarning(r.dueDate, r.status);
                    const displayCompany =
                      r.company || r.companyId?.companyName || "Bilinmiyor";
                    const hasPaymentsData =
                      r.hasAdvancePayment ||
                      (r.payments && r.payments.length > 0);
                    const isExpanded = expandedRows[r._id];
                    const currentAmount =
                      r.remainingAmount !== undefined
                        ? r.remainingAmount
                        : r.amount;

                    return (
                      <Fragment key={r._id}>
                        <tr className={warning ? warning.rowClass : ""}>
                          <td className="px-3">
                            <div className="fw-bold text-dark">
                              {displayCompany}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {r.country || r.companyId?.region}
                            </div>
                          </td>
                          <td className="px-3 fw-bold">{r.order}</td>
                          <td className="px-3">
                            <div>{formatDateTR(r.dueDate)}</div>
                            {warning && (
                              <div className="mt-1">
                                <Badge
                                  bg={warning.variant}
                                  text={
                                    warning.variant === "warning"
                                      ? "dark"
                                      : "light"
                                  }
                                  style={{ fontSize: "10px" }}
                                >
                                  {warning.text}
                                </Badge>
                              </div>
                            )}
                          </td>
                          <td className="text-end px-3">
                            {r.amount ? (
                              <span
                                className={`fw-bold ${
                                  currentAmount === 0
                                    ? "text-success"
                                    : "text-primary"
                                }`}
                                style={{ fontSize: "14px" }}
                              >
                                {fmtNum(currentAmount)} {r.currency}
                              </span>
                            ) : (
                              <span
                                className="text-muted fst-italic"
                                style={{ fontSize: "11px" }}
                              >
                                Gizli
                              </span>
                            )}
                          </td>
                          <td className="text-center px-3">
                            {renderBadge(r.status)}
                          </td>
                          <td
                            className="px-3 text-truncate"
                            style={{ maxWidth: "200px" }}
                            title={r.explaination}
                          >
                            {r.explaination}
                          </td>
                          <td className="text-center px-2">
                            {hasPaymentsData && canGetDetail ? (
                              <Button
                                size="sm"
                                variant={
                                  isExpanded ? "secondary" : "outline-secondary"
                                }
                                style={{ fontSize: "11px", padding: "2px 8px" }}
                                onClick={() => toggleRowDetails(r._id)}
                              >
                                {isExpanded ? "Gizle" : "Goster"}
                              </Button>
                            ) : (
                              <span
                                className="text-muted"
                                style={{ fontSize: "11px" }}
                              >
                                Yok
                              </span>
                            )}
                          </td>
                          {canUpdate && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-decoration-none"
                                style={{ fontSize: "12px" }}
                                onClick={() => {
                                  setEditRow(r);
                                  const extractedCid =
                                    r.companyId?._id ||
                                    r.companyId?.id ||
                                    (typeof r.companyId === "string"
                                      ? r.companyId
                                      : "");
                                  setForm({
                                    ...initialFormState,
                                    ...r,
                                    companyId: extractedCid,
                                    company:
                                      r.company ||
                                      r.companyId?.companyName ||
                                      "",
                                    amount: r.amount || "",
                                    payments: r.payments || [],
                                  });
                                  setShowModal(true);
                                }}
                              >
                                DUZENLE
                              </Button>
                            </td>
                          )}
                          {canDelete && (
                            <td className="text-center px-2">
                              <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-decoration-none text-danger"
                                style={{ fontSize: "12px" }}
                                onClick={() => setDeleteRow(r)}
                              >
                                SIL
                              </Button>
                            </td>
                          )}
                        </tr>

                        {isExpanded && hasPaymentsData && (
                          <tr className="bg-light">
                            <td
                              colSpan={tableColSpan}
                              className="p-0 border-bottom border-primary border-2"
                            >
                              <div className="p-3 m-2 bg-white rounded shadow-sm border">
                                <h6 className="fw-bold text-secondary border-bottom pb-2 mb-3">
                                  Siparis Odeme Dokumu ({r.order})
                                </h6>
                                <Row>
                                  <Col md={4} className="border-end">
                                    <div className="text-muted small mb-1">
                                      Genel Toplam Tutar
                                    </div>
                                    <div className="fw-bold fs-6">
                                      {fmtNum(r.amount)} {r.currency}
                                    </div>
                                    <div className="text-muted small mt-3 mb-1">
                                      Kalan Bakiye
                                    </div>
                                    <div className="fw-bold fs-5 text-primary">
                                      {fmtNum(currentAmount)} {r.currency}
                                    </div>
                                  </Col>
                                  <Col md={8}>
                                    {r.hasAdvancePayment && (
                                      <div className="mb-3">
                                        <span className="badge bg-info text-dark mb-2">
                                          On Odeme
                                        </span>
                                        <div className="d-flex justify-content-between small border-bottom pb-1">
                                          <span>
                                            Tarih:{" "}
                                            <strong>
                                              {formatDateTR(
                                                r.advancePaymentDate
                                              )}
                                            </strong>
                                          </span>
                                          <span className="text-success fw-bold">
                                            {fmtNum(r.advancePaymentAmount)}{" "}
                                            {r.currency}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    {r.payments && r.payments.length > 0 && (
                                      <div>
                                        <span className="badge bg-secondary mb-2">
                                          Parcali Odemeler
                                        </span>
                                        <Table
                                          size="sm"
                                          borderless
                                          className="mb-0 small"
                                        >
                                          <tbody>
                                            {r.payments.map((p, idx) => (
                                              <tr
                                                key={idx}
                                                className="border-bottom border-light"
                                              >
                                                <td
                                                  className="text-muted"
                                                  width="120"
                                                >
                                                  {formatDateTR(p.paidAt)}
                                                </td>
                                                <td>{p.note || "-"}</td>
                                                <td className="text-end text-success fw-bold">
                                                  {fmtNum(p.amount)}{" "}
                                                  {r.currency}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      </div>
                                    )}
                                  </Col>
                                </Row>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="text-center py-4 text-muted"
                    >
                      Kayit bulunamadi veya mevcut filtrelere uygun veri yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>

        {!isLoading && sortedAndFilteredRows.length > 0 && (
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border-top">
            <small className="text-muted">
              Toplam <b>{totalRecords}</b> kayittan{" "}
              <b>
                {(currentPage - 1) * 25 + 1} -{" "}
                {Math.min(currentPage * 25, totalRecords)}
              </b>{" "}
              arasi.
            </small>
            <Pagination size="sm" className="m-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  totalPages > 10 &&
                  Math.abs(currentPage - pageNum) > 3 &&
                  pageNum !== 1 &&
                  pageNum !== totalPages
                )
                  return null;
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card>

      {!isLoading && sortedAndFilteredRows.length > 0 && canGetDetail && (
        <div className="mt-4 mb-5">
          <h5
            className="fw-bold text-secondary mb-3"
            style={{ letterSpacing: "-0.5px" }}
          >
            Gosterilen Kayitlarin Finansal Ozeti
          </h5>
          <Row className="g-3">
            {Object.keys(analytics).length === 0 ? (
              <Col>
                <p className="text-muted small">
                  Analiz edilecek tutar bilgisi bulunamadi.
                </p>
              </Col>
            ) : (
              Object.entries(analytics).map(([currency, data]) => (
                <Col lg={3} md={4} sm={6} key={currency}>
                  <Card
                    className="border-0 shadow-sm h-100"
                    style={{
                      borderRadius: "10px",
                      borderTop: "4px solid #1976d2",
                    }}
                  >
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted fw-bold small">
                          {currency} TOPLAM
                        </span>
                        <Badge bg="primary" className="fs-6">
                          {fmtNum(data.total)}
                        </Badge>
                      </div>
                      <hr className="my-2" style={{ opacity: 0.1 }} />
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">Bekleyen (Pending):</span>
                        <span className="fw-bold text-warning">
                          {fmtNum(data.pending)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">Odenen (Paid):</span>
                        <span className="fw-bold text-success">
                          {fmtNum(data.paid)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">Riskli:</span>
                        <span className="fw-bold text-danger">
                          {fmtNum(data.risky)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span className="text-muted">Iptal Edilen:</span>
                        <span className="fw-bold text-secondary">
                          {fmtNum(data.cancelled)}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))
            )}
          </Row>
        </div>
      )}

      {/* MODALLAR BURADAN BASLIYOR */}

      {/* 1. Ekleme / Duzenleme Modali */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editRow ? "Kaydi Duzenle" : "Yeni Kayit Ekle"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Sirket Seciniz</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Select
                      value={form.companyId || ""}
                      onChange={handleSelectCustomer}
                    >
                      <option value="">-- Seciniz --</option>
                      {customers.map((c) => (
                        <option key={c._id || c.id} value={c._id || c.id}>
                          {c.companyName}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-success"
                      onClick={() => setShowCustomerModal(true)}
                      title="Yeni Musteri Ekle"
                    >
                      +
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Order No</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.order}
                    onChange={(e) =>
                      setForm({ ...form, order: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {canGetDetail && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Tutar</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) =>
                        setForm({ ...form, amount: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Para Birimi</Form.Label>
                    <Form.Select
                      value={form.currency}
                      onChange={(e) =>
                        setForm({ ...form, currency: e.target.value })
                      }
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="TRY">TRY</option>
                      <option value="GBP">GBP</option>
                      <option value="AED">AED</option>
                      <option value="SAR">SAR</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Vade Tarihi</Form.Label>
                    <Form.Control
                      type="date"
                      value={getFormDate(form.dueDate)}
                      onChange={(e) =>
                        setForm({ ...form, dueDate: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Durum</Form.Label>
                  <Form.Select
                    value={form.status}
                    onChange={(e) =>
                      setForm({ ...form, status: e.target.value })
                    }
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="risky">Risky</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Aciklama</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.explaination}
                    onChange={(e) =>
                      setForm({ ...form, explaination: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {canGetDetail && (
              <>
                <hr />
                <h6 className="fw-bold">On Odeme Bilgileri</h6>
                <Form.Check
                  type="checkbox"
                  label="On Odeme Alindi mi?"
                  checked={form.hasAdvancePayment}
                  onChange={(e) =>
                    setForm({ ...form, hasAdvancePayment: e.target.checked })
                  }
                  className="mb-2"
                />
                {form.hasAdvancePayment && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>On Odeme Tutari</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={form.advancePaymentAmount}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              advancePaymentAmount: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>On Odeme Tarihi</Form.Label>
                        <Form.Control
                          type="date"
                          value={getFormDate(form.advancePaymentDate)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              advancePaymentDate: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <hr />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold m-0">Parcali Odemeler</h6>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={handleAddPayment}
                  >
                    Odeme Ekle
                  </Button>
                </div>
                {form.payments && form.payments.length > 0 ? (
                  form.payments.map((p, idx) => (
                    <Row key={idx} className="align-items-end mb-2">
                      <Col md={3}>
                        <Form.Label className="small">Tutar</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={p.amount}
                          onChange={(e) =>
                            handlePaymentChange(idx, "amount", e.target.value)
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small">Tarih</Form.Label>
                        <Form.Control
                          size="sm"
                          type="date"
                          value={getFormDate(p.paidAt)}
                          onChange={(e) =>
                            handlePaymentChange(idx, "paidAt", e.target.value)
                          }
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small">Not</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={p.note || ""}
                          onChange={(e) =>
                            handlePaymentChange(idx, "note", e.target.value)
                          }
                        />
                      </Col>
                      <Col md={1}>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleRemovePayment(idx)}
                        >
                          X
                        </Button>
                      </Col>
                    </Row>
                  ))
                ) : (
                  <div className="text-muted small">
                    Parcali odeme bulunmamaktadir.
                  </div>
                )}
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Iptal
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 2. Yeni Musteri Ekleme Modali */}
      <Modal
        show={showCustomerModal}
        onHide={() => setShowCustomerModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Yeni Musteri Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Sirket Adi</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={customerForm.companyName}
                onChange={handleCustomerChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bolge / Ulke</Form.Label>
              <Form.Control
                type="text"
                name="region"
                value={customerForm.region}
                onChange={handleCustomerChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sektor</Form.Label>
              <Form.Control
                type="text"
                name="sector"
                value={customerForm.sector}
                onChange={handleCustomerChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCustomerModal(false)}
          >
            Iptal
          </Button>
          <Button
            variant="success"
            onClick={handleSaveCustomer}
            disabled={savingCustomer}
          >
            {savingCustomer ? "Ekleniyor..." : "Ekle"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 3. Silme Onay Modali */}
      <Modal
        show={deleteRow !== null}
        onHide={() => setDeleteRow(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Kayit Silme Onayi</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{deleteRow?.order}</strong> numarali siparise ait kaydi
            silmek istediginize emin misiniz? Bu islem geri alinamaz.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteRow(null)}>
            Vazgec
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Siliniyor..." : "Evet, Sil"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default PaymentDueListe;
